'use client'

import { createContext, type ReactNode, useContext } from 'react'

import { Language } from '@/lang'

interface LanguageContextValue {
  locale: Language
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

interface LanguageProviderProps {
  locale: Language
  children: ReactNode
}

/**
 * Language Provider
 *
 * Provides the current locale to all child components via React Context.
 *
 * @example
 * ```tsx
 * // In layout.tsx
 * <LanguageProvider locale={locale}>
 *   {children}
 * </LanguageProvider>
 *
 * // In any component
 * const { locale } = useLanguage()
 * ```
 */
export function LanguageProvider({ locale, children }: LanguageProviderProps) {
  return <LanguageContext.Provider value={{ locale }}>{children}</LanguageContext.Provider>
}

/**
 * Hook to access the current language/locale
 *
 * @returns Current locale (e.g., 'en', 'es')
 * @throws Error if used outside LanguageProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { locale } = useLanguage()
 *
 *   const { data } = await sanityFetch({
 *     query: postsQuery,
 *     params: { language: locale }
 *   })
 * }
 * ```
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext)

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }

  return context
}
